// Tenantra Inc. - Team Management Page
// Professional + Enterprise Feature - Multi-user Accounts
import { useState, useEffect } from 'react';
import { UserPlus, X, Pencil, Check, Loader2, Crown, Shield, Trash2, Mail, Users, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { getCurrentUser } from '@/services/auth';
import api from '@/services/api';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import { toast } from 'sonner';
import { formatDate } from '@/utils/date';

const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', member: 'Member' };

const StatusText = ({ status }) => {
  const map = {
    active: 'text-emerald-700', pending: 'text-amber-700', expired: 'text-slate-400', declined: 'text-red-700',
  };
  return <span className={`text-xs font-medium ${map[status] || 'text-slate-500'}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
};

const SectionHeader = ({ title, count, children }) => (
  <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
      {title}{typeof count === 'number' ? <span className="ml-1.5 text-slate-400 normal-case tracking-normal">({count})</span> : null}
    </span>
    {children}
  </div>
);

const AVATAR_COLORS = [
  'bg-teal-100 text-teal-700', 'bg-amber-100 text-amber-700', 'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700', 'bg-rose-100 text-rose-700', 'bg-emerald-100 text-emerald-700',
];
const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';
const avatarColor = (seed = '') => AVATAR_COLORS[(seed.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const Avatar = ({ name, email, owner }) => (
  <div className={`relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${avatarColor(email || name)}`}>
    {initials(name)}
    {owner && <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-teal-600 ring-2 ring-white flex items-center justify-center"><Crown className="w-2 h-2 text-white" /></span>}
  </div>
);

const AccessChips = ({ permissions, labelMap, isOwner, total }) => {
  if (isOwner) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium"><Shield className="w-3 h-3" />Full access</span>;
  }
  const perms = permissions || [];
  if (perms.length === 0) return <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px]">No access yet</span>;
  if (perms.length === total) return <span className="px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-[11px] font-medium">All sections</span>;
  const shown = perms.slice(0, 3);
  const extra = perms.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((p) => (
        <span key={p} className="px-2 py-0.5 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-[11px] font-medium">{labelMap[p] || p}</span>
      ))}
      {extra > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium">+{extra}</span>}
    </div>
  );
};

function ConfirmDialog({ open, onOpenChange, title, description, actionLabel, destructive, onConfirm, loading }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-white max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-slate-600">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="h-9 text-sm" data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); onConfirm(); }}
            disabled={loading}
            className={`h-9 text-sm text-white ${destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-600 hover:bg-teal-700'}`}
            data-testid="confirm-action-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const MemberRow = ({ member, isSelf, canManage, sectionCount, labelMap, onEditAccess, onRemove }) => {
  const editable = canManage && member.role !== 'owner' && !isSelf;
  const isOwner = member.role === 'owner';

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 transition-colors" data-testid={`member-row-${member.email}`}>
      <Avatar name={member.name} email={member.email} owner={isOwner} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-slate-900 truncate">{member.name}</p>
          {isSelf && <span className="text-[10px] font-medium text-slate-400 px-1.5 py-0.5 rounded bg-slate-100">You</span>}
          {member.last_login && <span className="hidden md:inline text-[11px] text-slate-400">· active {formatDate(member.last_login)}</span>}
        </div>
        <p className="text-xs text-slate-500 truncate">{member.email}</p>
        <div className="mt-1.5">
          <AccessChips permissions={member.permissions} labelMap={labelMap} isOwner={isOwner} total={sectionCount} />
        </div>
      </div>
      {editable ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => onEditAccess(member)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-teal-300 hover:text-teal-700 transition-colors"
            data-testid={`edit-access-${member.email}`}
          >
            <Pencil className="w-3 h-3" />Edit access
          </button>
          <button
            type="button"
            onClick={() => onRemove(member)}
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            aria-label={`Remove ${member.name}`}
            data-testid={`remove-member-${member.email}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        isOwner && <span className="shrink-0 text-[11px] font-medium text-slate-400 px-2 py-1 rounded-md bg-slate-50">Owner</span>
      )}
    </div>
  );
};

const InvitationRow = ({ invitation, labelMap, total, onResend, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); await fn(invitation.id); setBusy(false); };
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 transition-colors" data-testid={`invitation-row-${invitation.email}`}>
      <div className="w-9 h-9 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
        <Mail className="w-4 h-4 text-amber-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-900 truncate">{invitation.email}</p>
          <StatusText status={invitation.status} />
        </div>
        <p className="text-[11px] text-slate-400">Invited {formatDate(invitation.invited_at)}</p>
        <div className="mt-1.5"><AccessChips permissions={invitation.permissions} labelMap={labelMap} isOwner={false} total={total} /></div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" disabled={busy} onClick={() => run(onResend)} className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs font-medium text-teal-700 hover:border-teal-300 disabled:opacity-50" data-testid={`resend-invite-${invitation.email}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Resend'}
        </button>
        <button type="button" disabled={busy} onClick={() => run(onCancel)} className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50" aria-label="Cancel invitation" data-testid={`cancel-invite-${invitation.email}`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const InviteModal = ({ isOpen, onClose, teamId, sections, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const toggle = (key) => setPerms((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const allKeys = sections.map((s) => s.key);
  const allOn = allKeys.length > 0 && allKeys.every((k) => perms.includes(k));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post(`/teams/${teamId}/invite`, { email, permissions: perms });
      toast.success(res.data?.email_sent ? `Invitation sent to ${email}` : `Invitation created for ${email}`);
      onSuccess();
      onClose();
      setEmail('');
      setPerms([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl z-50 border border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="invite-modal">
        <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-slate-900">Invite a team member</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md" aria-label="Close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-10 bg-white border-slate-300" data-testid="invite-email-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-slate-700">What can they access?</Label>
              <button type="button" onClick={() => setPerms(allOn ? [] : allKeys)} className="text-xs text-teal-700 hover:text-teal-800" data-testid="invite-select-all">
                {allOn ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5 border border-slate-200 rounded-lg p-2">
              {sections.map((s) => (
                <label key={s.key} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer" data-testid={`invite-perm-${s.key}`}>
                  <input type="checkbox" checked={perms.includes(s.key)} onChange={() => toggle(s.key)} className="w-4 h-4 accent-teal-600" />
                  <span className="text-sm text-slate-700">{s.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 h-9 bg-teal-600 hover:bg-teal-700 text-white text-sm" data-testid="invite-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send invitation'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

const EditAccessModal = ({ isOpen, onClose, teamId, member, sections, onSuccess }) => {
  const [perms, setPerms] = useState(member?.permissions || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setPerms(member?.permissions || []); }, [member]);
  if (!isOpen || !member) return null;

  const toggle = (key) => setPerms((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const allKeys = sections.map((s) => s.key);
  const allOn = allKeys.length > 0 && allKeys.every((k) => perms.includes(k));

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/teams/${teamId}/members/${member.id}/permissions`, { permissions: perms });
      toast.success(`Access updated for ${member.name}`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl z-50 border border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="edit-access-modal">
        <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Edit access</h2>
            <p className="text-xs text-slate-500 mt-0.5">{member.name} · {member.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md" aria-label="Close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-slate-700">Dashboard sections</Label>
            <button type="button" onClick={() => setPerms(allOn ? [] : allKeys)} className="text-xs text-teal-700 hover:text-teal-800" data-testid="edit-select-all">
              {allOn ? 'Clear all' : 'Select all'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-1.5 border border-slate-200 rounded-lg p-2">
            {sections.map((s) => (
              <label key={s.key} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer" data-testid={`edit-perm-${s.key}`}>
                <input type="checkbox" checked={perms.includes(s.key)} onChange={() => toggle(s.key)} className="w-4 h-4 accent-teal-600" />
                <span className="text-sm text-slate-700">{s.label}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Cancel</Button>
            <Button type="button" onClick={save} disabled={loading} className="flex-1 h-9 bg-teal-600 hover:bg-teal-700 text-white text-sm" data-testid="edit-access-save-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save access'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const CreateTeamModal = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/teams', { name });
      toast.success('Team created');
      onSuccess();
      onClose();
      setName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl z-50 border border-slate-200" data-testid="create-team-modal">
        <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Create a team</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md" aria-label="Close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Team name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={100} className="h-10 bg-white border-slate-300" data-testid="team-name-input" />
            <p className="text-xs text-slate-500">Usually your company or portfolio name. You can change it later.</p>
          </div>
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 h-9 bg-teal-600 hover:bg-teal-700 text-white text-sm" data-testid="create-team-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create team'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

const PendingInvitationBanner = ({ invitation, onDone }) => {
  const [loading, setLoading] = useState(false);
  const act = async (action) => {
    setLoading(true);
    try {
      await api.post(`/teams/invitations/${invitation.token}/${action}`);
      toast.success(action === 'accept' ? `You joined ${invitation.team_name}` : 'Invitation declined');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} invitation`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="pending-invitation-banner">
      <p className="flex-1 text-sm text-slate-700">
        <span className="font-medium text-slate-900">{invitation.inviter_name}</span> invited you to join <span className="font-medium text-slate-900">{invitation.team_name}</span> as {ROLE_LABEL[invitation.role]?.toLowerCase()}.
        <span className="text-slate-500"> Expires {formatDate(invitation.expires_at)}.</span>
      </p>
      <div className="flex gap-2">
        <Button onClick={() => act('decline')} variant="outline" disabled={loading} className="h-8 text-xs">Decline</Button>
        <Button onClick={() => act('accept')} disabled={loading} className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Accept'}
        </Button>
      </div>
    </div>
  );
};

function TeamNameEditor({ team, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(team.name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (name.trim().length < 2 || name === team.name) { setEditing(false); setName(team.name); return; }
    setSaving(true);
    try {
      await api.put(`/teams/${team.id}`, { name: name.trim() });
      toast.success('Team name updated');
      onSaved();
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update team name');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setName(team.name); } }}
          className="h-8 w-64 text-sm bg-white border-slate-300" data-testid="team-name-edit-input" />
        <button type="button" onClick={save} disabled={saving} className="p-1.5 rounded-md text-teal-700 hover:bg-teal-50" aria-label="Save" data-testid="team-name-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button type="button" onClick={() => { setEditing(false); setName(team.name); }} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" aria-label="Cancel">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 group">
      <h1 className="text-sm font-semibold text-slate-900" data-testid="team-name">{team.name}</h1>
      {canEdit && (
        <button type="button" onClick={() => setEditing(true)} className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" aria-label="Rename team" data-testid="team-name-edit-btn">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default function TeamPage() {
  const me = getCurrentUser();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState({ team: null, role: null });
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [myInvitations, setMyInvitations] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sections, setSections] = useState([]);
  const [editMember, setEditMember] = useState(null);
  const [billingPlan, setBillingPlan] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, description, actionLabel, destructive, run }
  const [confirmBusy, setConfirmBusy] = useState(false);

  useEffect(() => { checkBillingPlan(); }, []);

  const checkBillingPlan = async () => {
    try {
      const response = await api.get('/billing/current-plan');
      setBillingPlan(response.data);
      const params = new URLSearchParams(window.location.search);
      const action = params.get('action');
      const token = params.get('token');
      if (action && token) handleInvitationAction(action, token); else fetchData();
    } catch (err) {
      console.error('Failed to check billing plan:', err);
      fetchData();
    }
  };

  const handleInvitationAction = async (action, token) => {
    try {
      if (action === 'accept') {
        await api.post(`/teams/invitations/${token}/accept`);
        toast.success('You joined the team');
      } else if (action === 'decline') {
        await api.post(`/teams/invitations/${token}/decline`);
        toast.success('Invitation declined');
      }
      window.history.replaceState({}, '', '/landlord/team');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} invitation`);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const teamRes = await api.get('/teams');
      setTeamData(teamRes.data);
      if (teamRes.data.team) {
        const manage = teamRes.data.role === 'owner' || teamRes.data.role === 'admin';
        const [membersRes, invitationsRes, sectionsRes] = await Promise.all([
          api.get(`/teams/${teamRes.data.team.id}/members`),
          manage ? api.get(`/teams/${teamRes.data.team.id}/invitations`) : Promise.resolve({ data: { invitations: [] } }),
          api.get('/teams/sections'),
        ]);
        setMembers(membersRes.data.members || []);
        setInvitations(invitationsRes.data.invitations || []);
        setSections(sectionsRes.data.sections || []);
      }
      const myInvitesRes = await api.get('/teams/invitations/my');
      setMyInvitations(myInvitesRes.data.invitations || []);
    } catch (err) {
      console.error('Failed to fetch team data:', err);
    } finally {
      setLoading(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try { await confirm.run(); } finally { setConfirmBusy(false); setConfirm(null); }
  };

  const askRemoveMember = (member) => setConfirm({
    title: `Remove ${member.name}?`,
    description: `${member.email} will lose access to this team's properties, tenants and reports immediately. If their login was created through a team invitation, the account is deleted.`,
    actionLabel: 'Remove member', destructive: true,
    run: async () => {
      try {
        await api.delete(`/teams/${teamData.team.id}/members/${member.id}`);
        toast.success(`${member.name} removed`);
        fetchData();
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove member'); }
    },
  });

  const askLeaveTeam = () => {
    const mine = members.find(m => m.user_id === me?.id);
    if (!mine) return;
    setConfirm({
      title: `Leave ${teamData.team.name}?`,
      description: 'You will lose access to the team\'s properties, tenants and reports. An owner or admin can invite you again later.',
      actionLabel: 'Leave team', destructive: true,
      run: async () => {
        try {
          await api.delete(`/teams/${teamData.team.id}/members/${mine.id}`);
          toast.success('You left the team');
          fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to leave team'); }
      },
    });
  };

  const askDeleteTeam = () => setConfirm({
    title: `Delete ${teamData.team.name}?`,
    description: `All ${members.length - 1} member${members.length - 1 === 1 ? '' : 's'} lose access and every outstanding invitation is cancelled. Your own properties, tenants and reports are not affected. This cannot be undone.`,
    actionLabel: 'Delete team', destructive: true,
    run: async () => {
      try {
        await api.delete(`/teams/${teamData.team.id}`);
        toast.success('Team deleted');
        fetchData();
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete team'); }
    },
  });

  const handleResendInvitation = async (invitationId) => {
    try {
      const res = await api.post(`/teams/${teamData.team.id}/invitations/${invitationId}/resend`);
      toast.success(res.data?.email_sent ? 'Invitation resent' : 'Invitation renewed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await api.delete(`/teams/${teamData.team.id}/invitations/${invitationId}`);
      toast.success('Invitation cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const canManage = teamData.role === 'owner' || teamData.role === 'admin';
  const isOwner = teamData.role === 'owner';
  const labelMap = Object.fromEntries(sections.map((s) => [s.key, s.label]));
  const pendingCount = invitations.filter((i) => i.status === 'pending').length;

  if (loading) return <TableSkeleton rows={5} spans={[5, 2, 2, 2, 1]} />;

  if (!teamData.team) {
    return (
      <div className="space-y-4 max-w-2xl" data-testid="team-empty-state">
        {myInvitations.map((inv) => <PendingInvitationBanner key={inv.id} invitation={inv} onDone={fetchData} />)}

        <div className="relative overflow-hidden bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-teal-50 to-transparent" />
          <div className="relative px-6 pt-8 pb-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-teal-600 shadow-lg shadow-teal-600/20 flex items-center justify-center mx-auto mb-4">
              <Users className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 tracking-tight">Bring your colleagues on board</h2>
            <Button onClick={() => setShowCreateModal(true)} className="mt-5 bg-teal-600 hover:bg-teal-700 text-white h-10 px-6 text-sm font-semibold rounded-lg" data-testid="create-team-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Create a team
            </Button>
          </div>
        </div>

        <CreateTeamModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={fetchData} />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="team-page">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <TeamNameEditor team={teamData.team} canEdit={isOwner} onSaved={fetchData} />
            <p className="text-xs text-slate-500 mt-1">Created {formatDate(teamData.team.created_at)} · You are the {ROLE_LABEL[teamData.role]?.toLowerCase()}</p>
          </div>
          {canManage && (
            <Button onClick={() => setShowInviteModal(true)} className="bg-teal-600 hover:bg-teal-700 text-white h-9 text-sm px-4 rounded-lg shrink-0" data-testid="invite-member-btn">
              <UserPlus className="w-4 h-4 mr-1.5" /> Invite member
            </Button>
          )}
        </div>
        <div className="flex gap-6 mt-4 pt-4 border-t border-slate-100">
          <div>
            <p className="text-xl font-bold text-slate-900 leading-none">{members.length}</p>
            <p className="text-[11px] text-slate-500 mt-1">Member{members.length !== 1 ? 's' : ''}</p>
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900 leading-none">{pendingCount}</p>
            <p className="text-[11px] text-slate-500 mt-1">Pending invite{pendingCount !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <SectionHeader title="Members" count={members.length} />
        <div className="divide-y divide-slate-100">
          {members.map((member) => (
            <MemberRow key={member.id} member={member} isSelf={member.user_id === me?.id} canManage={canManage} sectionCount={sections.length} labelMap={labelMap} onEditAccess={setEditMember} onRemove={askRemoveMember} />
          ))}
        </div>
      </div>

      {canManage && invitations.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <SectionHeader title="Pending invitations" count={invitations.length} />
          <div className="divide-y divide-slate-100">
            {invitations.map((inv) => (
              <InvitationRow key={inv.id} invitation={inv} labelMap={labelMap} total={sections.length} onResend={handleResendInvitation} onCancel={handleCancelInvitation} />
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-medium text-slate-700">{isOwner ? 'Delete team' : 'Leave team'}</span>
          <Button variant="outline" onClick={isOwner ? askDeleteTeam : askLeaveTeam} className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 sm:w-auto w-full" data-testid={isOwner ? 'delete-team-btn' : 'leave-team-btn'}>
            {isOwner ? 'Delete team' : 'Leave team'}
          </Button>
        </div>
      </div>

      <InviteModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} teamId={teamData.team?.id} sections={sections} onSuccess={fetchData} />
      <EditAccessModal isOpen={!!editMember} onClose={() => setEditMember(null)} teamId={teamData.team?.id} member={editMember} sections={sections} onSuccess={fetchData} />
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o && !confirmBusy) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        actionLabel={confirm?.actionLabel}
        destructive={confirm?.destructive}
        onConfirm={runConfirm}
        loading={confirmBusy}
      />
    </div>
  );
}
